"use client"

import { useState } from "react"

import { useAnalyticsEvent, useGetCustomer } from "@unlikelystudio/react-ecommerce-hooks"

import { getProductCustomAttributesValue } from "~/lib/shopify/constants"
import { getReferenceIdFromGID } from "~/lib/shopify/utils/id"
import { useCustomAnalyticsEvent } from "~/hooks/tracking/useCustomAnalyticsEvent"
import useCurrency from "~/hooks/useCurrency"
import { useGetCart } from "~/hooks/useGetCart"
import useLocale from "~/hooks/useLocale"
import type { CartLineItemPayload, PageName } from "~/components/shared/AnalyticsEvents/types"
import { useShouldPushEvents } from "~/components/shared/AnalyticsEvents/use-should-push-events"
import { safeJSONParse } from "~/utils/safe-json-parse"

export function AnalyticsEvents({ pageName }: { pageName: PageName }) {
  const locale = useLocale()
  const splitLocale = locale.split("-")
  const lang = splitLocale?.[0] ?? undefined
  const market = splitLocale?.[1]?.toUpperCase() ?? undefined

  const currency = useCurrency()

  const { cartPayload } = useGetCart()

  const { data: customer } = useGetCustomer()

  const [pageviewEventSent, setPageviewEventSent] = useState(false)

  // tracking consent is handled in GTM
  // https://app.clickup.com/t/2688547/DAREL-3101?comment=90150129156616
  const hasUserConsent = true
  const isReadyToPushEvents = useShouldPushEvents()

  const canProcessEvent = hasUserConsent && isReadyToPushEvents

  const defaultEventPayload = {
    market,
    locale: lang,
  }

  const ecommerceDefaultEventPayload = {
    content_type: "Product",
    currency: currency.toUpperCase(),
    page_type: pageName,
    value: cartPayload?.cost.totalAmount.amount ?? 0,
  }

  useAnalyticsEvent<"pageview">({
    type: "pageview",
    canProcessEvent,
    callback: () => {
      if (window?.dataLayer) {
        window?.dataLayer?.push({
          ...defaultEventPayload,
          event: "page_view",
          page_type: pageName,
          virtual_page_location: document.URL,
          virtual_page_title: document.title,
          virtual_page_referrer: document.referrer,
          user_email: customer?.email ?? null,
          user_id: getReferenceIdFromGID(customer?.id) ?? null,
        })

        setPageviewEventSent(true)
      }
    },
  })

  useCustomAnalyticsEvent<"view_item">({
    type: "view_item",
    canProcessEvent: canProcessEvent && pageviewEventSent,
    callback: (event) => {
      if (window?.dataLayer) {
        window?.dataLayer?.push({ ecommerce: null })

        window?.dataLayer?.push({
          ...defaultEventPayload,
          event: "view_item",
          ecommerce: {
            ...ecommerceDefaultEventPayload,
            value: event?.items?.[0]?.price ?? 0,
            items: event?.items,
          },
        })
      }
    },
  })

  useCustomAnalyticsEvent<"view_item_list">({
    type: "view_item_list",
    canProcessEvent: canProcessEvent && pageviewEventSent,
    callback: (event) => {
      if (window?.dataLayer) {
        window?.dataLayer?.push({ ecommerce: null })

        window?.dataLayer?.push({
          ...defaultEventPayload,
          event: "view_item_list",
          ecommerce: {
            ...ecommerceDefaultEventPayload,
            ...event?.ecommerce,
          },
        })
      }
    },
  })

  useCustomAnalyticsEvent<"add_to_cart">({
    type: "add_to_cart",
    canProcessEvent: canProcessEvent && pageviewEventSent,
    callback: async (event) => {
      const { cartPayload, fromKleep, variants: activeVariants } = event

      const variants = (Array.isArray(activeVariants) ? activeVariants : [activeVariants]).filter(Boolean)

      const lines =
        variants
          ?.map((variant) => cartPayload?.lines.find((line) => line.merchandise?.id === variant?.id))
          ?.flatMap((item) => (item?.id ? [item] : [])) ?? []

      if (lines?.length === 0) return

      if (window?.dataLayer) {
        window?.dataLayer?.push({ ecommerce: null })

        const linesPayload = lines.map((line) => {
          const trackingData = getProductCustomAttributesValue(line?.attributes, "TRACKING_DATA")?.value ?? "{}"
          const parsedTrackingData = safeJSONParse<CartLineItemPayload>(trackingData)
          if (!parsedTrackingData) return null
          return { ...parsedTrackingData, quantity: line?.quantity }
        })

        window?.dataLayer?.push({
          ...defaultEventPayload,
          event: "add_to_cart",

          ecommerce: {
            ...ecommerceDefaultEventPayload,
            ...(fromKleep ? { source: "kleep" } : {}),
            value: linesPayload.reduce((acc, line) => acc + (line?.price ?? 0), 0),
            items: linesPayload,
          },
        })
      }

      if (window?.kleep) {
        try {
          cartPayload?.lines.map((line) => {
            window.kleep.track("product_added_to_cart", {
              productId: getReferenceIdFromGID(line.merchandise?.product?.id),
              variantId: getReferenceIdFromGID(line.merchandise?.id),
              cart: cartPayload?.lines.map((line) => ({
                productId: getReferenceIdFromGID(line.merchandise?.product?.id),
                variantId: getReferenceIdFromGID(line.merchandise?.id),
                sku: line.merchandise?.sku,
                size: line.merchandise?.title,
                quantity: line.quantity,
                price: line.merchandise?.price,
              })),
            })
          })
        } catch {
          console.error("Error tracking product added to cart")
        }
      }
    },
  })

  useCustomAnalyticsEvent<"view_cart">({
    type: "view_cart",
    canProcessEvent: canProcessEvent && pageviewEventSent,
    callback: (event) => {
      if (window?.dataLayer) {
        window?.dataLayer?.push({ ecommerce: null })

        window?.dataLayer?.push({
          ...defaultEventPayload,
          event: "view_cart",
          ecommerce: {
            ...ecommerceDefaultEventPayload,
            items:
              event?.cartPayload?.lines?.map((line) => {
                const trackingData = getProductCustomAttributesValue(line?.attributes, "TRACKING_DATA")?.value ?? ""
                const parsedTrackingData = safeJSONParse<CartLineItemPayload>(trackingData)

                return {
                  ...parsedTrackingData,
                  quantity: line?.quantity ?? 0,
                }
              }) ?? [],
          },
        })
      }
    },
  })

  useCustomAnalyticsEvent<"remove_from_cart">({
    type: "remove_from_cart",
    canProcessEvent: canProcessEvent && pageviewEventSent,
    callback: (event) => {
      if (window?.dataLayer) {
        window?.dataLayer?.push({ ecommerce: null })

        const linesPayload = event?.cartPayload?.lines.map((line) => {
          const trackingData = getProductCustomAttributesValue(line?.attributes, "TRACKING_DATA")?.value ?? ""
          const parsedTrackingData = safeJSONParse<CartLineItemPayload>(trackingData)

          return { ...parsedTrackingData, quantity: line?.quantity ?? 0 }
        })

        const { value: _, ...rest } = ecommerceDefaultEventPayload

        window?.dataLayer?.push({
          ...defaultEventPayload,
          event: "remove_from_cart",
          ecommerce: {
            ...rest,
            items: linesPayload,
          },
        })
      }
    },
  })

  useAnalyticsEvent<"search">({
    type: "search",
    canProcessEvent: canProcessEvent && pageviewEventSent,
    callback: (event) => {
      if (window?.dataLayer) {
        window?.dataLayer?.push({ ecommerce: null })

        window?.dataLayer?.push({
          ...defaultEventPayload,
          event: "search",
          search_term: event?.searchTerm,
        })
      }
    },
  })

  return null
}
